import { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  Truck,
  FileText,
  CreditCard,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";

import AnimatedChart from "../Components/AnimatedChart";
import DashboardLayout from "../Layouts/DashboardLayout";

export default function VendorDashboardHome() {
  const [chartType, setChartType] = useState("orders");
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    fetch("/api/vendor/dashboard", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setDashboard(data);
        }
      });
  }, []);

  const alerts = useMemo(() => {
    if (!dashboard) return [];

    const a = [];

    if (dashboard.stats.pending_deliveries > 0) {
      a.push({
        type: "warning",
        text: `${dashboard.stats.pending_deliveries} purchase orders pending delivery`,
      });
    }

    if (dashboard.stats.outstanding > 0) {
      a.push({
        type: "danger",
        text: `₹${dashboard.stats.outstanding.toLocaleString()} payment pending`,
      });
    }

    return a;
  }, [dashboard]);

  return (
    <DashboardLayout>
      <div className="space-y-10">

        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <KpiCard
            label="Purchase Orders"
            value={dashboard?.stats.purchase_orders ?? 0}
          />

          <KpiCard
            label="Pending Deliveries"
            value={dashboard?.stats.pending_deliveries ?? 0}
          />

          <KpiCard
            label="Approved Invoices"
            value={dashboard?.stats.approved_invoices ?? 0}
          />

          <KpiCard
            label="Payments Received"
            value={`₹${(dashboard?.stats.payments_received ?? 0).toLocaleString()}`}
          />
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FinanceCard
            title="Total Invoiced (MTD)"
            value={`₹${(dashboard?.stats.total_invoiced ?? 0).toLocaleString()}`}
          />

          <FinanceCard
            title="Payments Received"
            value={`₹${(dashboard?.stats.payments_received ?? 0).toLocaleString()}`}
          />

          <FinanceCard
            title="Outstanding Amount"
            value={`₹${(dashboard?.stats.outstanding ?? 0).toLocaleString()}`}
            danger
          />
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <ChartBox title="Orders & Deliveries">
            <ChartHeader
              options={["orders", "deliveries", "payments"]}
              active={chartType}
              setActive={setChartType}
            />
            <AnimatedChart type={chartType} />
          </ChartBox>

          <ChartBox title="Monthly Payment Trend">
            <AnimatedChart type="vendor-payments" />
          </ChartBox>
        </section>

        <section className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h3 className="font-serif text-lg mb-4">Vendor Alerts</h3>

          {alerts.length === 0 ? (
            <div className="text-emerald-400 text-sm">
              All deliveries and payments are on track
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((a, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm
                  ${
                    a.type === "danger"
                      ? "bg-red-500/10 text-red-400"
                      : "bg-amber-400/10 text-amber-400"
                  }`}
                >
                  <AlertTriangle size={16} />
                  {a.text}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-4">Recent Activity</h3>
            <ul className="space-y-3 text-sm text-gray-300">
              {dashboard?.recent_activity.map((a, i) => (
                <li key={i}>
                  • {a.po} - {a.status}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-4">Upcoming Deliveries</h3>
            <ul className="space-y-3 text-sm text-gray-300">
              {dashboard?.upcoming_deliveries.map((d, i) => (
                <li key={i}>
                  • {d.po} — {new Date(d.date).toLocaleDateString()}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function ChartBox({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-8">
      <h3 className="font-serif text-lg mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ChartHeader({ options, active, setActive }) {
  return (
    <div className="flex gap-2 mb-4">
      {options.map((t) => (
        <button
          key={t}
          onClick={() => setActive(t)}
          className={`px-4 py-1.5 text-xs rounded-lg transition
          ${
            active === t
              ? "bg-amber-400 text-black font-semibold"
              : "bg-black/40 text-gray-400 hover:text-white"
          }`}
        >
          {t.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:border-amber-400/40 transition">
      <p className="text-xs text-gray-400 tracking-widest uppercase">{label}</p>
      <h2 className="mt-3 text-3xl font-serif">{value}</h2>
    </div>
  );
}

function ActionButton({ icon: Icon, label }) {
  return (
    <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black/40 border border-white/10 text-sm hover:bg-white/10 transition">
      <Icon size={16} />
      {label}
    </button>
  );
}

function FinanceCard({ title, value, danger }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        danger
          ? "bg-red-500/10 border-red-500/30"
          : "bg-white/5 border-white/10"
      }`}
    >
      <p className="text-xs text-gray-400 tracking-widest uppercase">{title}</p>
      <h3 className={`mt-3 text-2xl font-serif ${danger && "text-red-400"}`}>
        {value}
      </h3>
    </div>
  );
}
